// Anim registry — binds ctx.animDataFor to the SHIPPING .anir metadata (sim/data/anir.json,
// extracted from htdata by the wi.cl pipeline). Strip timing is SIM STATE: frame counts ×
// delays decide when looping/once-through anims end, which times kmidAnimationComplete,
// AnimGob self-deletion (gid release order!), and firing-strip durations.
//
// Field mapping (wi.cl anir.json -> AnimData, both raw bytes from the .anir file):
//   strip.delay  -> AnimStripData.defaultDelay   (StripData.cDelay)
//   frame.hold   -> AnimFrameData.delay          (FrameData.cHold)
//   frame.s1/s2  -> special1/special2            (bCustomData1/2)

import { AnimData, type AnimStripData } from '../anim/animation.ts';
import { knVersionSimulation } from '../simversion.ts';

interface WiClAnirFrame {
  ibm: number;
  ibm2: number;
  hold: number;
  x: number;
  y: number;
  x2: number;
  y2: number;
  s1: number;
  s2: number;
}
interface WiClAnirStrip {
  name: string;
  delay: number;
  loop: boolean;
  frames: WiClAnirFrame[];
}
export type AnirJson = Record<string, WiClAnirStrip[]>;

/** gcxTile for the 832 art pack (32px tiles) — a sim input to GetSpecialPoint32. */
export const kcxTile832 = 32;

// simVersion<5 data compat — the andy.anir strip-order fix (tasks/sim-quirks.md D3). Until v5
// every shipped pack's andy.anir carried the art2432 recovery's alphabetical strip order (die,
// fire 00-15, ICON at 17, idle 00-15, move 00-15), and AndyGob/FoxGob's strip tables (idle
// 17-32 / move 33-48, game/Andy.cpp) mis-indexed it: idle dir-0 played the 1-frame icon, move
// dir-0 played 'idle 15', every other idle/move strip was one direction off. v5 fixed the DATA
// (icon moved last — the lri/sri layout), which changes the frame counts/holds the sim times
// against (kmidAnimationComplete, m_unvl MinSkip), so a pre-v5 recording/snapshot must re-sim
// against the order it was recorded with — rebuild it here.
function legacyAndyStripOrder(strips: WiClAnirStrip[]): WiClAnirStrip[] {
  const icon = strips[strips.length - 1];
  if (icon === undefined || icon.name !== 'icon') return strips; // already the legacy layout
  return [...strips.slice(0, 17), icon, ...strips.slice(17, -1)];
}

/** Returns an animDataFor implementation over the anir registry. Names arrive as the C++
 *  passes them ("mhq.anir", case per GobTemplates); the registry keys are bare lowercase.
 *  `simVersion` is read lazily (first use per animation) because Sim.restore only learns the
 *  snapshot's version mid-stream — after the registry is bound, before any gob binds an anim. */
export function makeAnimRegistry(
  anir: AnirJson,
  gcxTile: number = kcxTile832,
  simVersion: () => number = () => knVersionSimulation,
): (szAniName: string) => AnimData {
  const cache = new Map<string, AnimData>();
  return (szAniName: string): AnimData => {
    const key = szAniName.toLowerCase().replace(/\.anir$/, '');
    let pd = cache.get(key);
    if (pd === undefined) {
      let strips = anir[key];
      if (strips === undefined) throw new Error(`anir registry: no animation '${szAniName}'`);
      if (key === 'andy' && simVersion() < 5) strips = legacyAndyStripOrder(strips);
      const mapped: AnimStripData[] = strips.map((s) => ({
        name: s.name,
        defaultDelay: s.delay,
        frames: s.frames.map((f) => ({ delay: f.hold, special1: f.s1, special2: f.s2 })),
      }));
      pd = new AnimData(mapped, gcxTile);
      pd.name = key;
      cache.set(key, pd);
    }
    return pd;
  };
}
